import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Bot, Copy, KeyRound, Loader2, ShieldAlert, Trash2 } from "lucide-react";
import { adminApi, apiError } from "../adminApi";
import { Panel, Pill, buttonClass, inputClass } from "../components/Bits";

const when = (stamp) => (stamp ? new Date(stamp).toLocaleString(undefined, {
  day: "numeric", month: "short", hour: "2-digit", minute: "2-digit",
}) : "never");

const copyText = async (text, label) => {
  try {
    await navigator.clipboard.writeText(text);
    toast.success(`${label} copied`);
  } catch {
    toast.error("Copying is blocked here — select the text and copy it manually");
  }
};

export default function AdminBotAccess() {
  const [data, setData] = useState(null);
  const [audit, setAudit] = useState([]);
  const [name, setName] = useState("Grok bot");
  const [chosen, setChosen] = useState([]);
  const [issued, setIssued] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    adminApi.get("/crm/bot-tokens").then(({ data: payload }) => {
      setData(payload);
      setChosen((prev) => (prev.length ? prev : Object.keys(payload.scopes)));
    }).catch(() => {});
    adminApi.get("/crm/bot-audit?limit=25")
      .then(({ data: payload }) => setAudit(payload.events)).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggle = (scope) => setChosen((prev) =>
    prev.includes(scope) ? prev.filter((s) => s !== scope) : [...prev, scope]);

  const create = async () => {
    if (!chosen.length) {
      toast.error("Pick at least one thing the bot may do");
      return;
    }
    setBusy(true);
    try {
      const { data: payload } = await adminApi.post("/crm/bot-tokens",
        { name: name.trim() || "Bot", scopes: chosen });
      setIssued(payload);
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (tokenId, tokenName) => {
    try {
      await adminApi.post(`/crm/bot-tokens/${tokenId}/revoke`);
      toast.success(`${tokenName} can no longer reach the desk`);
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  if (!data) {
    return (
      <div className="flex items-center gap-2 text-sm text-cream/50" data-testid="bot-loading">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading bot access…
      </div>
    );
  }

  const live = data.tokens.filter((t) => !t.revoked_at);

  return (
    <div className="space-y-8" data-testid="admin-bot-access">
      <header>
        <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-gold/70">
          Machine access
        </p>
        <h1 className="mt-2 font-serif text-4xl font-light text-cream sm:text-5xl">
          Bot access
        </h1>
        <p className="mt-3 max-w-2xl text-sm leading-relaxed text-cream/55">
          Give an outside bot its own key into the desk. Each key carries only the permissions
          you tick, every call it makes is logged below, and you can cut it off at any moment
          without touching your own login.
        </p>
      </header>

      {issued ? (
        <div className="rounded-2xl border border-gold/40 bg-gold/5 p-6" data-testid="bot-token-issued">
          <p className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-gold">
            <ShieldAlert className="h-3.5 w-3.5" /> Copy this now — it is shown once
          </p>
          <p className="mt-3 text-sm leading-relaxed text-cream/70">
            Paste it into your bot's configuration. We only keep a one-way fingerprint, so it
            cannot be shown again. If you lose it, revoke the key and issue another.
          </p>
          <div className="mt-4 flex flex-wrap items-center gap-3">
            <code className="break-all rounded border border-cream/15 bg-[#0B1710] px-3 py-2 text-[11px] text-cream/80"
                  data-testid="bot-token-value">
              {issued.token}
            </code>
            <button type="button" className={buttonClass} data-testid="bot-token-copy"
                    onClick={() => copyText(issued.token, "Token")}>
              <Copy className="h-3.5 w-3.5" /> Copy
            </button>
          </div>
          <button type="button" data-testid="bot-token-dismiss"
                  onClick={() => setIssued(null)}
                  className="mt-4 font-mono text-[10px] uppercase tracking-[0.2em] text-cream/40 transition-colors duration-200 hover:text-cream">
            I've saved it
          </button>
        </div>
      ) : null}

      <Panel title="Issue a key" testid="panel-issue-key">
        <label className="block space-y-1.5">
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-cream/40">
            What is it for
          </span>
          <input value={name} onChange={(e) => setName(e.target.value)} className={inputClass}
                 data-testid="bot-name-input" placeholder="Grok bot" />
        </label>

        <div className="mt-5 space-y-2">
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-cream/40">
            What it may do
          </p>
          {Object.entries(data.scopes).map(([scope, blurb]) => (
            <label key={scope} data-testid={`bot-scope-${scope.replace(":", "-")}`}
                   className="flex cursor-pointer items-start gap-3 rounded-lg border border-cream/10 p-3 transition-colors duration-200 hover:border-gold/40">
              <input type="checkbox" checked={chosen.includes(scope)}
                     onChange={() => toggle(scope)} className="mt-1 accent-[#C8A24A]" />
              <span>
                <span className="block text-sm text-cream/80">{blurb}</span>
                <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-cream/30">
                  {scope}
                </span>
              </span>
            </label>
          ))}
        </div>

        <button type="button" onClick={create} disabled={busy} className={`${buttonClass} mt-5`}
                data-testid="bot-create-btn">
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <KeyRound className="h-3.5 w-3.5" />}
          Issue key
        </button>
      </Panel>

      <Panel title={`Keys (${live.length} live)`} testid="panel-bot-tokens">
        <ul className="divide-y divide-cream/10">
          {data.tokens.map((token) => (
            <li key={token.token_id} className="py-3" data-testid={`bot-token-${token.token_id}`}>
              <div className="flex flex-wrap items-baseline justify-between gap-3">
                <p className="flex items-center gap-2 text-sm text-cream/85">
                  <Bot className="h-3.5 w-3.5 text-gold/70" /> {token.name}
                </p>
                <Pill tone={token.revoked_at ? "terra" : "gold"}>
                  {token.revoked_at ? "revoked" : "live"}
                </Pill>
              </div>
              <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.18em] text-cream/30">
                {token.calls || 0} calls · last used {when(token.last_used_at)} · issued{" "}
                {when(token.created_at)}
              </p>
              <p className="mt-1 text-xs text-cream/45">{token.scopes.join(" · ")}</p>
              {!token.revoked_at ? (
                <button type="button" data-testid={`bot-revoke-${token.token_id}`}
                        onClick={() => revoke(token.token_id, token.name)}
                        className="mt-2 inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-cream/35 transition-colors duration-200 hover:text-terra">
                  <Trash2 className="h-3 w-3" /> Revoke
                </button>
              ) : null}
            </li>
          ))}
          {data.tokens.length === 0 ? (
            <li className="py-3 text-sm text-cream/40" data-testid="bot-tokens-empty">
              No keys yet. Issue one above and paste it into your bot.
            </li>
          ) : null}
        </ul>
      </Panel>

      <Panel title="What the bot has been doing" testid="panel-bot-audit">
        <ul className="divide-y divide-cream/10">
          {audit.map((event, index) => (
            <li key={index} className="flex flex-wrap items-baseline justify-between gap-3 py-2.5"
                data-testid="bot-audit-row">
              <p className="text-sm text-cream/75">
                <span className="text-cream/45">{event.token_name}</span> · {event.action}
                {event.detail ? (
                  <span className="text-cream/40"> — {event.detail}</span>
                ) : null}
              </p>
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-cream/30">
                {when(event.at)}
              </p>
            </li>
          ))}
          {audit.length === 0 ? (
            <li className="py-3 text-sm text-cream/40" data-testid="bot-audit-empty">
              Nothing yet. Every lookup, note and booking your bot makes shows up here.
            </li>
          ) : null}
        </ul>
      </Panel>
    </div>
  );
}
